import React, { lazy, Suspense, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Switch, BrowserRouter as Router, Redirect, browserHistory  } from "react-router-dom";
import { StylesProvider, createGenerateClassName } from '@material-ui/styles';
import withWidth from '@material-ui/core/withWidth';
import {Helmet} from "react-helmet";
import i18next from 'i18next';
import toast from "ui/Toast";
import { LAYOUTS } from 'constant';
import { privateRoutes, publicRoutes } from "constant/routes";

/* Import Styles */
import './assets/css/app.scss';

/* Routes */
import PrivateRoute from 'components/routes/PrivateRoute';
import PublicRoute from 'components/routes/PublicRoute';

/* Services */
import { getAuthToken, deleteAuthToken, setLoginInfo } from 'services/Auth.service';
import { AuthApi } from "services/AuthAPI.service";
import { getOrgCode, getSubdomain, logOutSteps } from "helpers/common.helper"
import { deleteUserNotHavePermission, getUserNotHavePermission } from 'services/LocalStorage.service';

/* redux */
import { RootState } from "store/state";
import { setLoggedIn, setUserInfo, setLogoNonLogin, setCustomerPortal } from "store/modules/user/user.action";
import { setIsRegisteredOrg, setLoadingOrg, setOrganisationData } from "store/modules/organisation/organisation.action";
import { setTrackedList } from "store/modules/track/track.action";

/* Component */
import DelayedFallback from 'components/utils/DelayedFallback';
const CompactHeader = lazy(() => import("components/layout/CompactHeader"));
const Sidebar = lazy(() => import("components/layout/SideBar"));
const SideBarMobileComponent = lazy(() => import("components/layout/SideBarMobile"));
const HeaderMobileComponent = lazy(() => import("components/layout/HeaderMobile"));

const generateClassName = createGenerateClassName({
  productionPrefix: 'Q',
  seed: 'Q-root',
});

const layouts = [
  LAYOUTS.DEFAULT,
  LAYOUTS.AUTH, // For the pages of authentication eg: login, signup,...,
  LAYOUTS.COMPACT, // Header + hamberger menu for the side bar
]

const Main = (props: any) => {
  const AuthApiInstance = new AuthApi();
  const [layout, setLayout] = useState(layouts[1]);
  const [showMenu, setShowMenu] = useState(false);
  const [isShowMenuMobile, setShowMenuMobile] = useState(false);
  const [isShowMenuMobileAuth, setShowMenuMobileAuth] = useState(true);
  const [isMobileMode, setIsMobileMode] = React.useState(null);
  const [isAuthMode, setIsAuthMode] = React.useState(null);
  const [backPath, setBackPath] = useState("/track");
  const {
    logos,
    setLogoNonLogin,
    setIsRegisteredOrg,
    setLoadingOrg,
    isRegisteredOrg,
    customerPortal,
    setLoggedIn,
    setCustomerPortal,
    isLoggedIn,
    setUserInfo,
    setOrganisationData,
    userPermissions
  } = props;

  const getUserInfo = async () => {
    const response = await AuthApiInstance.getProfile();
    if (response) {
      setUserInfo(response);
      const { permissions } = response;
      if (permissions?.length) {
        const isGuestRole = permissions.some(item => item.name === "customer_portal");
        if (isGuestRole) setCustomerPortal(true);
        else logOutSteps(null, true);
      }
    }
  }

  // get customer portal permission to check if user can access QShip
  const checkCustomerPortalPermission = async () => {
    if (isLoggedIn) {
      const authToken = localStorage.getItem("loginToken");
      const response = await AuthApiInstance.getCustomerPortalPermission(authToken);
      if (response) {
        const { customer_portal, message } = response;
        if (!customer_portal) {
          setLoggedIn(false);
          await AuthApiInstance.logout();
          localStorage.removeItem("userInfo");
          deleteAuthToken();
          if (localStorage.getItem("logout_url")) {
            logOutSteps(null, message.split(" ").join(","));
          } else {
            toast.error(message);
          }
        } else {
          setCustomerPortal(true);
        }
      }
    }
  }

  useEffect(() => {
    if(!localStorage.getItem("i18nextLng"))
      i18next.changeLanguage("en-US")

    if (isLoggedIn) {
      try {
        getUserInfo();
        // checkCustomerPortalPermission();
      } catch (error) {}
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get("error")
    if (error) {
      toast.error(error.split(",").join(" "));
    }

    const authtokenParam = urlParams ? urlParams.get('authtoken') : null;
    if (authtokenParam && !isLoggedIn) {
      // Save tokens and expiry times to local storage for SSO login case
      const data = {
        id: urlParams.get("userId"),
        token: authtokenParam,
        token_expires_at: urlParams.get("token_expires_at"),
        refresh_token: urlParams.get("refresh_token"),
        refresh_token_expires_at: urlParams.get("refresh_token_expires_at"),
      }
      setLoginInfo(data);

      const logoutUrl = urlParams.get("logout_url");
      if (logoutUrl) localStorage.setItem("logout_url", logoutUrl);
      setLoggedIn(true);
    }

  }, []);

  useEffect(() => {
    let authToken = getAuthToken();
    if (authToken) {
      props.setLoggedIn(true);
    } else {
      props.setLoggedIn(false);
    }

    // Show error message if user does not have permission to access QSHIP
    const userNotHavePermission = getUserNotHavePermission();
    if (userNotHavePermission) {
      toast.error(userNotHavePermission);
      deleteUserNotHavePermission();
    }
  }, []);

  const setLayoutHandler = (layoutName: string = 'default') => {
    setTimeout(() => {
      setLayout(layoutName);
    }, 0);
  };

  const onIconClickFromSidebar = () => {
    if (layout === "compact") setShowMenu(false)
  }

  const onIconClickFromCompactHeader = () => {
    const showMenuTmp = showMenu;
    if (isMobileMode) setShowMenuMobile(!isShowMenuMobile);
    else setShowMenu(!showMenuTmp);
  }

  React.useEffect(() => {
    if(!props.width) return;
    if(props.width === "xl" || props.width === "xldesktop") {
      setIsMobileMode(false);
    } else {
      setIsMobileMode(true);
    }
  }, [props.width])

  React.useEffect(() => {
    if(!props.width) return;
    if(props.width === "xl" || props.width === "xldesktop" || props.width === "sl" || props.width === "lg" || props.width ==="slaptop") {
      setIsAuthMode(false);
    } else {
      setIsAuthMode(true);
    }
  }, [props.width])

  const showMenuMobile = () => {
    setShowMenuMobile(!isShowMenuMobile);
  }

  const showMenuMobileAuth = () => {
    setShowMenuMobileAuth(!isShowMenuMobileAuth);
  }

  useEffect(() => {
    if (layout === "compact") {
      setShowMenu(false)
      setShowMenuMobile(false)
    }
  }, [layout])

  useEffect(() => {
    async function getLogoNonLogin() {
      if (isRegisteredOrg) {
        const url = window.location.hostname;
        const organisation = getSubdomain(url);
        const response = await AuthApiInstance.getLogoNonLogin(organisation);
        if (response) setLogoNonLogin(response);
      }
    }
    getLogoNonLogin();
  }, [isRegisteredOrg]);

  // Check if Organisation is registered
  useEffect(() => {
    async function getRegisteredOrganisations() {
      try {
        // we use localhost domain is for Cypress testing
        // as at the moment still not find out the way to run custom QSHIP domain for bitbucket pipeline
        const localDomains = ["quincus.com", "quincus.qship.local.com", "localhost"];
        const url = window.location.hostname;
        const subDomain = getOrgCode(url);

        const param =
          localDomains.some(item => url.includes(item))
            ? `?code=${subDomain}`
            : `?white_labeled_url_qship=${url}`;
        const response = await AuthApiInstance.getRegisteredOrganisations(param);
        if (!response) setIsRegisteredOrg(false);
        else {
          setOrganisationData({
            ...response,
            features: response.features || [],
            isShipmentV1: (response.modules || []).find((m) => m.name === "shipment")?.version === "v1",
          });
          setIsRegisteredOrg(true);
          const { code, white_labeled_url } = response;
          localStorage.setItem("orgCode", code);
          if (white_labeled_url) localStorage.setItem("qPortal", white_labeled_url.trim());
        }
      } catch (error) {
        setIsRegisteredOrg(false);
      } finally {
        setLoadingOrg(false);
      }
    }

    getRegisteredOrganisations();
  }, []);

  return (
    <StylesProvider generateClassName={generateClassName}>
      {logos?.short_logo ? (
        <Helmet>
          <link rel="shortcut icon" type="image/png" sizes="16x16" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="57x57" href={logos.short_logo}/>
          <link rel="apple-touch-icon-precomposed" sizes="57x57" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="60x60" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="72x72" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="76x76" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="114x114" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="120x120" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="144x144" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="152x152" href={logos.short_logo}/>
          <link rel="apple-touch-icon" sizes="180x180" href={logos.short_logo}/>
        </Helmet>
        ) : (
        <Helmet>
          <link rel="shortcut icon" type="image/png" href="/assets/illustration.png" sizes="16x16" data-react-helmet="true" />
          <link rel="apple-touch-icon" sizes="57x57" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon-precomposed" sizes="57x57" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="60x60" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="72x72" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="76x76" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="114x114" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="120x120" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="144x144" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="152x152" href="/assets/illustration.png"/>
          <link rel="apple-touch-icon" sizes="180x180" href="/assets/illustration.png"/>
        </Helmet>
      )}
      <div className={
        `page-wrapper ${layout === 'auth' ? 'simple-page' : layout === 'compact' ? 'page-wrapper secondary-page' : '' } ${layout === 'compact' && showMenu ? 'show-sidebar' : 'hide-sidebar'}`
      }>
        <Router history = {browserHistory}>
          {/* <HeaderComponent /> */}
          <Suspense fallback={<DelayedFallback />}>
            {layout !== layouts[1] ? (
              <>
                {/* Render left side bar for logged-in pages or track-non-login page */}
                {isMobileMode ? (
                  <SideBarMobileComponent
                    isShowMenuMobile={isShowMenuMobile}
                    setShowMenuMobile={showMenuMobile}
                    setBackPath={setBackPath}
                  />
                ) : (
                  <Sidebar
                    {...props}
                    onIconClick={onIconClickFromSidebar}
                    showSidebar={showMenu}
                    setBackPath={setBackPath}
                  />
                )}

                {/* Render mobile header for logged-in pages */}
                {isMobileMode && layout === layouts[0] && (
                  <HeaderMobileComponent
                    setShowMenuMobile={showMenuMobile}
                    isShowMenuMobile={isShowMenuMobile}
                    screenWidth={props.width}
                  />
                )}

                {/* Render compact header for pages that don't have left side bar */}
                {layout === layouts[2] && (
                  <CompactHeader
                    onIconClick={onIconClickFromCompactHeader}
                    backPath={backPath}
                    isShowMenuMobile={isShowMenuMobile}
                  />
                )}
              </>
            ) : (
              <>
                {/* Render mobile header for non-login pages */}
                {isRegisteredOrg && isAuthMode && (
                  <HeaderMobileComponent
                    setShowMenuMobile={showMenuMobileAuth}
                    isShowMenuMobile={isShowMenuMobileAuth}
                    screenWidth={props.width}
                  />
                )}
              </>
            )}
          </Suspense>

          <Suspense fallback={<DelayedFallback />}>
            <Switch>
              {/* Public Routes */}
              {publicRoutes.map(({ path, exact, component, layout, restricted }, index) => (
                <PublicRoute
                  key={index}
                  path={path}
                  exact={exact}
                  component={component}
                  layout={layout}
                  setLayout={setLayoutHandler}
                  isAuth={restricted && customerPortal && userPermissions}
                  userPermissions={userPermissions}
                />
              ))}

              {/* Private Routes */}
              {privateRoutes.map(({ path, component, exact, layout, isAccess, validatePermission }) => {
                const viewPermission = validatePermission(userPermissions);
                return (
                  <PrivateRoute
                    // key={path}
                    path={path}
                    component={component}
                    exact={exact}
                    layout={layout}
                    setLayout={setLayoutHandler}
                    isAccess={isAccess}
                    viewPermission={viewPermission}
                    userPermissions={userPermissions}
                  />
                );
              })}

              {/* Redirect when url isn't correct */}
              <Redirect to="/" />
            </Switch>
          </Suspense>
        </Router>
      </div>
    </StylesProvider>
  )
}

window.addEventListener("storage", function(event) {
  if (event.key == "loginToken") {
      window.location.reload();
  }
});

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: state.user.isLoggedIn,
  logos: state.user.logos,
  isRegisteredOrg: state.organisation.isRegisteredOrg,
  customerPortal: state.user.customerPortal,
  userPermissions: state.user.userInfo.permissions,
});

const mapDispatchToProps = {
  setLoggedIn,
  setUserInfo,
  setTrackedList,
  setLogoNonLogin,
  setIsRegisteredOrg,
  setLoadingOrg,
  setCustomerPortal,
  setOrganisationData,
};

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(Main));